import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import {
  GripVertical, ChevronUp, ChevronDown, Trash2, Plus, Save, Newspaper, Pin, Megaphone,
} from "lucide-react";
import RichTextEditor from "./RichTextEditor";
import { cn } from "../lib/utils";
import { fmtMDT } from "../lib/time";

function newPost() {
  return {
    id: `tmp-${Math.random().toString(36).slice(2, 9)}`,
    title: "",
    tag: "",
    body: "",
    date: "",
    url: "",
    show_from: "",
    archive_after: "",
    pinned: false,
    display_order: 100,
  };
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIso(local) {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function VigilNewsEditor() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openIdx, setOpenIdx] = useState(0); // which announcement is expanded

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vigil-news");
      setItems((data || []).map((n) => ({
        id: n.id,
        title: n.title || "",
        tag: n.tag || "",
        body: n.body || "",
        date: n.date || "",
        url: n.url || "",
        show_from: toDatetimeLocal(n.show_from),
        archive_after: toDatetimeLocal(n.archive_after),
        pinned: !!n.pinned,
        display_order: n.display_order ?? 100,
      })));
    } catch {
      toast({ title: "Failed to load Vigil News", variant: "destructive" });
    }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[idx], next[j]] = [next[j], next[idx]]; setItems(next);
    if (openIdx === idx) setOpenIdx(j);
    else if (openIdx === j) setOpenIdx(idx);
  };
  const patch = (idx, p) => { const next = [...items]; next[idx] = { ...next[idx], ...p }; setItems(next); };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const add = () => { setItems([newPost(), ...items]); setOpenIdx(0); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        items: items
          .filter((i) => i.title?.trim())
          .map((i, idx) => ({
            id: i.id?.startsWith("tmp-") ? null : i.id,
            title: i.title.trim(),
            tag: i.tag?.trim() || null,
            body: i.body || null,
            date: i.date?.trim() || null,
            url: i.url?.trim() || null,
            show_from: toIso(i.show_from),
            archive_after: toIso(i.archive_after),
            pinned: !!i.pinned,
            display_order: (idx + 1) * 10,
          })),
      };
      await api.put("/admin/vigil-news", payload);
      toast({ title: "Vigil News saved" });
      await load();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-5">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Newspaper className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h3 className="text-[15px] font-semibold">Vigil News</h3>
          <p className="text-[12px] text-muted-foreground">Internal announcements shown on the dashboard sidebar. Super Admin only.</p>
        </div>
        <div className="ml-auto">
          <Button size="sm" variant="outline" onClick={add} className="gap-1.5"><Plus className="w-4 h-4" /> New announcement</Button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((it, idx) => {
          const isOpen = openIdx === idx;
          return (
            <div key={it.id} className={cn("rounded-lg border transition-colors", isOpen ? "border-primary/40" : "border-border")}>
              {/* Row header */}
              <div className="flex items-start gap-2 p-3">
                <div className="text-muted-foreground pt-2"><GripVertical className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setOpenIdx(isOpen ? -1 : idx)}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {it.pinned && <Pin className="w-3.5 h-3.5 text-primary" />}
                    {it.tag && <span className="text-[10px] font-bold tracking-wider text-primary uppercase">{it.tag}</span>}
                    <span className="text-[13.5px] font-semibold text-foreground truncate">{it.title || "Untitled"}</span>
                  </div>
                  {(it.show_from || it.archive_after) && (
                    <div className="text-[11.5px] text-muted-foreground mt-0.5">
                      {it.show_from && <>from {fmtMDT(it.show_from)} </>}
                      {it.archive_after && <>· archive {fmtMDT(it.archive_after)}</>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => move(idx, -1)} disabled={idx === 0} className="h-8 w-8"><ChevronUp className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="h-8 w-8"><ChevronDown className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(idx)} className="h-8 w-8 text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-border p-4 space-y-4">
                  <div className="flex items-center gap-2 text-[13px] font-semibold">
                    <Megaphone className="w-4 h-4 text-primary" /> New announcement
                    <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 text-primary text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide">Internal · Super Admin</span>
                  </div>

                  <Input value={it.title} onChange={(e) => patch(idx, { title: e.target.value })} placeholder="Title" className="font-semibold text-[14px]" />

                  <RichTextEditor value={it.body} onChange={(v) => patch(idx, { body: v })} />

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[12px]">Tag</Label>
                      <Input value={it.tag} onChange={(e) => patch(idx, { tag: e.target.value })} placeholder="UPCOMING WEBINAR" className="mt-1 uppercase" />
                    </div>
                    <div>
                      <Label className="text-[12px]">Short date label</Label>
                      <Input value={it.date} onChange={(e) => patch(idx, { date: e.target.value })} placeholder="Jul 29" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-[12px]">Link (optional)</Label>
                      <Input value={it.url} onChange={(e) => patch(idx, { url: e.target.value })} placeholder="https://…" className="mt-1" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[12px]">Show from (optional)</Label>
                      <Input type="datetime-local" value={it.show_from} onChange={(e) => patch(idx, { show_from: e.target.value })} className="mt-1" />
                      <p className="mt-1 text-[11px] text-muted-foreground">Leave blank to publish immediately.</p>
                    </div>
                    <div>
                      <Label className="text-[12px]">Archive after (optional)</Label>
                      <Input type="datetime-local" value={it.archive_after} onChange={(e) => patch(idx, { archive_after: e.target.value })} className="mt-1" />
                      <p className="mt-1 text-[11px] text-muted-foreground">Post auto-hides after this time.</p>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={it.pinned} onCheckedChange={(v) => patch(idx, { pinned: !!v })} />
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-medium">
                      <Pin className="w-3.5 h-3.5 text-primary" /> Pin to top of Vigil News
                    </span>
                  </label>
                </div>
              )}
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border py-10 text-center text-muted-foreground text-[13px]">
            No announcements yet. Click <span className="font-medium text-foreground">New announcement</span> to draft one.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={add} className="gap-1.5"><Plus className="w-4 h-4" /> New announcement</Button>
        <Button onClick={save} disabled={busy} className="gap-2"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save Vigil News"}</Button>
      </div>
    </div>
  );
}
